import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TeamFarm {
  id: string;
  user_id: string;
  username: string | null;
  farm_name: string | null;
  bio: string | null;
  location: string | null;
  usda_zone: string | null;
  avatar_url: string | null;
  farm_logo_url: string | null;
  is_discoverable: boolean;
  team_name: string;
  team_id: string;
}

export const useMyTeamsFarms = (enabled: boolean = true) => {
  const [farms, setFarms] = useState<TeamFarm[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMyTeamsFarms = async () => {
    if (!enabled) {
      setFarms([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setFarms([]);
        return;
      }

      // Get user's profile ID
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (profileError) {
        throw profileError;
      }

      // Get all teams the user is a member of (either created or joined)
      const { data: userTeams, error: teamsError } = await supabase
        .from('teams')
        .select('id, name')
        .order('created_at', { ascending: false });

      if (teamsError) {
        throw teamsError;
      }

      if (!userTeams || userTeams.length === 0) {
        setFarms([]);
        return;
      }

      const teamIds = userTeams.map(t => t.id);

      // Get all members from these teams
      const { data: teamMembers, error: membersError } = await supabase
        .from('team_members')
        .select('user_id, team_id, teams!inner(id, name)')
        .in('team_id', teamIds);

      if (membersError) {
        throw membersError;
      }

      if (!teamMembers || teamMembers.length === 0) {
        setFarms([]);
        return;
      }

      // Get unique user IDs from team members
      const uniqueUserIds = [...new Set(teamMembers.map(m => m.user_id))];

      // Fetch profiles for these users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, user_id, username, farm_name, bio, location, usda_zone, avatar_url, farm_logo_url, is_discoverable, is_active')
        .in('id', uniqueUserIds)
        .eq('is_active', true)
        .eq('is_discoverable', true);

      if (profilesError) {
        throw profilesError;
      }

      // Map profiles to team farms with team information
      const teamFarms: TeamFarm[] = (profiles || []).flatMap(profile => {
        // Find all teams this profile is part of
        const profileTeams = teamMembers
          .filter(m => m.user_id === profile.id)
          .map(m => ({
            team_id: m.team_id,
            team_name: (m.teams as any)?.name || 'Unknown Team'
          }));

        // Create a farm entry for each team membership
        return profileTeams.map(team => ({
          id: profile.id,
          user_id: profile.user_id,
          username: profile.username,
          farm_name: profile.farm_name,
          bio: profile.bio,
          location: profile.location,
          usda_zone: profile.usda_zone,
          avatar_url: profile.avatar_url,
          farm_logo_url: profile.farm_logo_url,
          is_discoverable: profile.is_discoverable,
          team_name: team.team_name,
          team_id: team.team_id
        }));
      });

      setFarms(teamFarms);
    } catch (err: any) {
      console.error('Error fetching my teams farms:', err);
      setError(err.message || 'Failed to fetch team farms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyTeamsFarms();
  }, [enabled]);

  return {
    farms,
    loading,
    error,
    refetch: fetchMyTeamsFarms
  };
};
